import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { LocalizedLink } from './LocalizedLink'
import { LanguageSelector } from './LanguageSelector'
import { cn } from '@/lib/utils'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

export function Header() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const currentLang = i18n.language as SupportedLanguage

  const isActive = (key: string) => {
    const routeKey = key as RouteKey
    if (!(routeKey in localizedRoutes)) return false
    const localizedPath = localizedRoutes[routeKey][currentLang]
    const fullPath = localizedPath ? `/${currentLang}/${localizedPath}` : `/${currentLang}`
    // For home, exact match; for others, startsWith to catch sub-routes (e.g. products/*)
    if (routeKey === 'home') return location.pathname === fullPath || location.pathname === `${fullPath}/`
    return location.pathname.startsWith(fullPath)
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { key: 'home', label: t('nav.home') },
    { key: 'about', label: t('nav.about') },
    { key: 'method', label: t('nav.method') },
    { key: 'products', label: t('nav.products') },
    { key: 'skills', label: t('nav.skills') },
    { key: 'careers', label: t('nav.careers') },
    { key: 'contacts', label: t('nav.contacts') },
  ]

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-[999] transition-all duration-normal bg-dark',
        isScrolled
          ? 'bg-dark/95 backdrop-blur-sm shadow-lg py-3'
          : 'py-4'
      )}
    >
      <div className="max-w-[1448px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <LocalizedLink to="home" className="flex items-center">
            <img
              src="/images/logos/simertec_logo.png"
              alt="Simertec"
              className="h-10 md:h-12 w-auto bg-white rounded-lg px-3 py-1.5"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <LocalizedLink
                key={item.key}
                to={item.key}
                className={cn(
                  'font-medium transition-all duration-200 pb-1 border-b-2 hover:[text-shadow:0_0_12px_rgba(255,255,255,0.5)]',
                  isActive(item.key)
                    ? 'text-primary border-primary'
                    : 'text-white/90 border-transparent hover:text-primary'
                )}
              >
                {item.label}
              </LocalizedLink>
            ))}
          </nav>

          {/* Language Selector & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2"
              aria-label={isMobileMenuOpen ? t('aria.closeMenu') : t('aria.openMenu')}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.key}>
                  <LocalizedLink
                    to={item.key}
                    className={cn(
                      'block font-medium py-2 transition-colors border-l-3',
                      isActive(item.key)
                        ? 'text-primary border-primary pl-3'
                        : 'text-white/90 hover:text-primary border-transparent pl-3'
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
            <div className="border-t border-white/10 mt-4 pt-4">
              <LanguageSelector variant="mobile" />
            </div>
          </nav>
        )}
      </div>
    </header>
  )
}
